import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { ConfirmButton, Field, ImageField, MetaLine, SaveButton, StatusToggle, VideoField, inputCls } from "../../components/admin/fields";

const EMPTY = { title: "", gallery: "", external_url: "", media_type: "image", video: "", image: "", poster: "", image_alt: "", active: false, status: "published" };

export default function EditionsPage() {
  const [editions, setEditions] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/editions").then((res) => setEditions(res.data || [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (form.id) {
        await api.put(`/admin/editions/${form.id}`, form);
      } else {
        await api.post("/admin/editions", form);
      }
      setForm(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (edition) => {
    await api.put(`/admin/editions/${edition.id}`, { ...edition, active: true });
    load();
  };

  return (
    <div className="max-w-3xl" data-testid="admin-editions">
      <div className="flex items-center justify-between">
        <h1 className="h-condensed text-4xl">Latest Edition</h1>
        {!form && (
          <button type="button" data-testid="new-edition-button" onClick={() => setForm(EMPTY)} className="link-arrow text-xs">
            New edition
          </button>
        )}
      </div>
      <p className="mt-3 text-sm font-light opacity-70">Only one edition can be active. Activating one deactivates the others.</p>

      {!form && (
        <ul className="mt-8 divide-y divide-charcoal border-y border-charcoal">
          {editions.map((ed) => (
            <li key={ed.id} className="flex items-center justify-between gap-4 py-4" data-testid={`edition-row-${ed.id}`}>
              <div>
                <p className="h-condensed text-xl">{ed.title}</p>
                <p className="mt-1 text-xs font-light opacity-60">
                  {ed.gallery} · {ed.media_type}
                </p>
              </div>
              <div className="flex items-center gap-4">
                {ed.active ? (
                  <span className="eyebrow text-[0.65rem]" data-testid="edition-active-label">Active</span>
                ) : (
                  <button type="button" onClick={() => activate(ed)} data-testid={`edition-activate-${ed.id}`} className="link-arrow text-xs">
                    Set active
                  </button>
                )}
                <button type="button" onClick={() => setForm(ed)} data-testid={`edition-edit-${ed.id}`} className="link-arrow text-xs">
                  Edit
                </button>
                <ConfirmButton
                  label="Delete"
                  testid={`edition-delete-${ed.id}`}
                  onConfirm={async () => {
                    await api.delete(`/admin/editions/${ed.id}`);
                    load();
                  }}
                />
              </div>
            </li>
          ))}
          {editions.length === 0 && <li className="py-6 text-sm font-light opacity-60">No editions yet.</li>}
        </ul>
      )}

      {form && (
        <form onSubmit={save} className="mt-10 space-y-8" data-testid="edition-form">
          <Field label="Title">
            <input required value={form.title} onChange={set("title")} className={inputCls} data-testid="edition-title" />
          </Field>
          <Field label="Gallery">
            <input value={form.gallery || ""} onChange={set("gallery")} className={inputCls} data-testid="edition-gallery" />
          </Field>
          <Field label="External URL" hint="The LEARN MORE button opens this in a new tab.">
            <input value={form.external_url || ""} onChange={set("external_url")} className={inputCls} data-testid="edition-url" />
          </Field>
          <Field label="Media type">
            <select value={form.media_type || "image"} onChange={set("media_type")} className={inputCls} data-testid="edition-media-type">
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </Field>
          {form.media_type === "video" ? (
            <>
              <VideoField label="Banner video" value={form.video} onChange={set("video")} testid="edition-video" />
              <ImageField label="Poster image" value={form.poster} onChange={set("poster")} testid="edition-poster" />
            </>
          ) : (
            <ImageField label="Banner image" value={form.image} onChange={set("image")} alt={form.image_alt} onAltChange={set("image_alt")} testid="edition-image" />
          )}
          <div className="flex items-center gap-4">
            <StatusToggle value={form.status || "published"} onChange={set("status")} testid="edition-status-toggle" />
            <label className="flex items-center gap-2 font-condensed text-xs font-semibold uppercase tracking-[0.14em]">
              <input type="checkbox" checked={!!form.active} onChange={set("active")} data-testid="edition-active-toggle" className="h-4 w-4 accent-[#1E1E1E]" />
              Active on home page
            </label>
          </div>
          <MetaLine by={form.updated_by} at={form.updated_at} />
          <div className="flex items-center gap-6 border-t border-charcoal pt-6">
            <SaveButton busy={busy} dirty />
            <button type="button" onClick={() => setForm(null)} className="link-arrow text-xs" data-testid="edition-cancel">
              Cancel
            </button>
            {error && <p className="text-sm" role="alert">{error}</p>}
          </div>
        </form>
      )}
    </div>
  );
}
